import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2, History, RotateCcw, Save, Search, X, Pencil } from "lucide-react";

const fmt = (v, ccy = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: ccy }).format(v || 0);

/**
 * QuoteEditor — inline editing of a saved quote with full audit trail.
 *
 * Lets the partner:
 *   • change quantities per line (live preview of the new total),
 *   • remove existing lines,
 *   • add new SKUs via an autocompleter on the active price list,
 *   • commit changes — which creates a new revision V<n+1> server-side.
 *
 * Also surfaces the revisions panel: every snapshot since creation, with the
 * exact diff (added / removed / qty_changed) and a one-click "Restaurer" which
 * re-applies that revision's BOM through /revise so prices are refreshed.
 */
export default function QuoteEditor({ quote, onUpdated, onClose }) {
  const cfg = quote?.configuration || {};
  const ccy = cfg.currency || "USD";
  const initialLines = useMemo(
    () => (cfg.bom || []).map((b) => ({
      sku: b.sku,
      name: b.name,
      type: b.type,
      quantity: b.quantity,
      unit_price: (cfg.boq || []).find((q) => q.sku === b.sku)?.unit_price || 0,
    })),
    [cfg.bom, cfg.boq]
  );
  const [lines, setLines] = useState(initialLines);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  // SKU replacement mode — when set, the line at that index renders an inline
  // autocompleter instead of the static SKU text. Picking a result swaps the
  // SKU + name + unit_price in place (qty preserved).
  const [editingIdx, setEditingIdx] = useState(null);
  const [editSearch, setEditSearch] = useState("");
  const [editResults, setEditResults] = useState([]);
  const [editSearching, setEditSearching] = useState(false);
  const editInputRef = useRef(null);

  useEffect(() => { setLines(initialLines); }, [initialLines]);

  // Live preview total — uses the snapshot unit_price (no live re-pricing
  // until the partner saves; backend will then refresh prices from the active
  // price list and write the authoritative figure).
  const previewTotal = useMemo(
    () => lines.reduce((s, l) => s + (Number(l.unit_price) || 0) * (Number(l.quantity) || 0), 0),
    [lines]
  );
  const initialTotal = useMemo(
    () => initialLines.reduce((s, l) => s + (Number(l.unit_price) || 0) * (Number(l.quantity) || 0), 0),
    [initialLines]
  );
  const dirty = useMemo(() => {
    if (lines.length !== initialLines.length) return true;
    const byInitial = new Map(initialLines.map((l) => [l.sku, l.quantity]));
    // SKU swap = a line whose SKU is no longer in the initial set.
    if (lines.some((l) => !byInitial.has(l.sku))) return true;
    return lines.some((l) => byInitial.get(l.sku) !== l.quantity);
  }, [lines, initialLines]);

  // Add-ons flagged "addon_auto" by the engine (use-case driven indispensables)
  // that are NOT in the current edited BOM. Surfaced as a yellow alert above
  // the editor so the partner can spot them BEFORE clicking Save.
  const missingAddons = useMemo(() => {
    return (cfg.addon_recommendations || [])
      .filter((r) => r.kind === "addon_auto" && r.direct_sku)
      .filter((r) => !lines.find((l) => l.sku === r.direct_sku));
  }, [cfg.addon_recommendations, lines]);

  const setQty = (sku, qty) => {
    const q = Math.max(1, Math.min(100000, parseInt(qty || "1", 10) || 1));
    setLines((ls) => ls.map((l) => (l.sku === sku ? { ...l, quantity: q } : l)));
  };
  const removeLine = (sku) => {
    setLines((ls) => ls.filter((l) => l.sku !== sku));
  };

  // Open the inline SKU replacement mode for a given line index.
  const openEditSku = (idx) => {
    setEditingIdx(idx);
    setEditSearch("");
    setEditResults([]);
    // Defer focus to the next paint when the input is mounted.
    setTimeout(() => editInputRef.current?.focus(), 50);
  };
  const closeEditSku = () => {
    setEditingIdx(null);
    setEditSearch("");
    setEditResults([]);
  };
  // Replace the SKU + name + price of line at editingIdx with the picked PL row.
  // Keeps the original quantity. Refuses duplicates.
  const replaceSku = (item) => {
    if (editingIdx === null) return;
    const current = lines[editingIdx];
    if (!current) { closeEditSku(); return; }
    if (item.sku === current.sku) { closeEditSku(); return; }
    if (lines.some((l, i) => i !== editingIdx && l.sku === item.sku)) {
      toast.error("Cette référence est déjà dans la BOM. Modifiez la ligne existante ou choisissez un autre SKU.");
      return;
    }
    setLines((ls) =>
      ls.map((l, i) =>
        i === editingIdx
          ? {
              ...l,
              sku: item.sku,
              name: item.name || item.sku,
              type: item.item_type || l.type,
              unit_price: item.price || 0,
            }
          : l
      )
    );
    toast.success(`SKU remplacé · ${current.sku} → ${item.sku}`);
    closeEditSku();
  };
  const addSearched = (item) => {
    if (lines.find((l) => l.sku === item.sku)) {
      toast.error("Cette référence est déjà dans la BOM.");
      return;
    }
    setLines((ls) => [
      ...ls,
      {
        sku: item.sku,
        name: item.name,
        type: item.item_type || "Hardware",
        quantity: 1,
        unit_price: item.price || 0,
      },
    ]);
    setSearch("");
    setSearchResults([]);
  };

  // Debounced autocomplete on the active price list.
  useEffect(() => {
    if (!search || search.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(() => {
      setSearching(true);
      api.get("/pricelist/base/search", { params: { q: search, limit: 8 } })
        .then((r) => setSearchResults(r.data?.items || []))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  // Debounced autocomplete dedicated to the inline SKU replacement input.
  useEffect(() => {
    if (editingIdx === null) return;
    if (!editSearch || editSearch.length < 2) { setEditResults([]); return; }
    const t = setTimeout(() => {
      setEditSearching(true);
      api.get("/pricelist/base/search", { params: { q: editSearch, limit: 8 } })
        .then((r) => setEditResults(r.data?.items || []))
        .catch(() => setEditResults([]))
        .finally(() => setEditSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [editSearch, editingIdx]);

  const loadRevisions = async () => {
    try {
      const r = await api.get(`/quotes/${quote.id}/revisions`);
      setRevisions(r.data || []);
    } catch {
      toast.error("Impossible de charger l'historique");
    }
  };

  const saveChanges = async () => {
    // Insistent guard: surface every "addon_auto" recommendation whose SKU is
    // NOT in the current edit. Same logic as the configurator's red banner.
    if (missingAddons.length > 0) {
      const list = missingAddons
        .map((r) => `• ${r.name}${r.direct_sku ? ` (${r.direct_sku})` : ""}`)
        .join("\n");
      const proceed = window.confirm(
        `⚠ Vous avez ignoré ${missingAddons.length} add-on(s) indispensable(s) :\n\n${list}\n\nCes éléments ont été identifiés comme indispensables pour le projet (cas d'usage / typologie client). Confirmer la sauvegarde sans eux ?`
      );
      if (!proceed) return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/quotes/${quote.id}/revise`, {
        lines: lines.map((l) => ({ sku: l.sku, quantity: Number(l.quantity) })),
        comment: comment || undefined,
      });
      toast.success(`Révision V${r.data.revision} enregistrée`);
      setComment("");
      onUpdated && onUpdated(r.data);
      if (showHistory) loadRevisions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const restore = async (revision) => {
    if (!window.confirm(`Restaurer la révision V${revision} ? Les prix seront rafraîchis depuis la price list actuelle.`)) return;
    setSaving(true);
    try {
      const r = await api.post(`/quotes/${quote.id}/revisions/${revision}/restore`);
      toast.success(`Révision V${revision} restaurée — nouveau snapshot V${r.data.revision}`);
      onUpdated && onUpdated(r.data);
      loadRevisions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la restauration");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-zinc-200 bg-white rounded-sm" data-testid="quote-editor">
      <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.15em] uppercase text-[#D42B1E]">Édition du devis</p>
          <h3 className="mt-0.5 text-base font-semibold text-zinc-900">{quote.title}</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-mono text-zinc-500">
            V{quote.revision} · {quote.revisions_count} révision(s)
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => { setShowHistory((s) => !s); if (!showHistory) loadRevisions(); }}
            className="rounded-sm border-zinc-300"
            data-testid="quote-editor-toggle-history"
          >
            <History className="h-3.5 w-3.5 mr-1.5" />
            Historique
          </Button>
          {onClose && (
            <Button type="button" size="icon" variant="ghost" onClick={onClose} className="h-7 w-7" data-testid="quote-editor-close">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Missing-addons alert — surfaces use-case-driven indispensable add-ons
          that the partner has not yet added to the BOM. Visible BEFORE Save. */}
      {missingAddons.length > 0 && (
        <div
          className="mx-5 my-4 rounded-sm border-l-4 border-amber-500 bg-amber-50 p-4"
          data-testid="quote-editor-missing-addons"
          role="alert"
        >
          <p className="text-sm font-bold text-amber-900 uppercase tracking-wider mb-2">
            ⚠ {missingAddons.length} add-on(s) indispensable(s) non ajouté(s)
          </p>
          <p className="text-xs text-amber-900 mb-2 leading-relaxed">
            Le moteur a identifié ces éléments comme nécessaires pour le projet. Cliquez sur « Ajouter » pour chacun, ou confirmez explicitement la sauvegarde sans eux.
          </p>
          <ul className="space-y-1 text-xs">
            {missingAddons.map((r) => (
              <li
                key={r.direct_sku}
                className="flex items-center justify-between gap-2 bg-white/70 rounded-sm px-2 py-1.5"
                data-testid={`quote-editor-missing-${r.direct_sku}`}
              >
                <span className="min-w-0">
                  <span className="font-semibold text-amber-900">{r.name}</span>
                  <span className="ml-2 font-mono text-[10px] text-amber-700">{r.direct_sku}</span>
                  {r.direct_quantity > 1 && (
                    <span className="ml-2 font-mono text-[10px] text-amber-700">×{r.direct_quantity}</span>
                  )}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setLines((ls) => [
                      ...ls,
                      {
                        sku: r.direct_sku,
                        name: r.direct_name || r.direct_sku,
                        type: "Add-on",
                        quantity: Math.max(1, parseInt(r.direct_quantity || 1, 10) || 1),
                        unit_price: r.direct_price || 0,
                      },
                    ])
                  }
                  className="rounded-sm border-amber-400 bg-white text-amber-900 hover:bg-amber-100 h-7 px-2 text-[11px]"
                  data-testid={`quote-editor-add-missing-${r.direct_sku}`}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Ajouter
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Lines editor */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="quote-editor-lines">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Référence</th>
              <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Produit</th>
              <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Prix unit.</th>
              <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-zinc-500 w-32">Qté</th>
              <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Total</th>
              <th className="px-4 py-2.5 w-12" />
            </tr>
          </thead>
          <tbody>
            {lines.map((l, idx) => (
              <tr key={`${l.sku}-${idx}`} className="border-b border-zinc-100 align-top" data-testid={`quote-editor-line-${idx}`}>
                <td className="px-4 py-2 font-mono text-xs text-zinc-700 min-w-[200px]">
                  {editingIdx === idx ? (
                    <div className="relative" data-testid={`quote-editor-edit-sku-${idx}`}>
                      <div className="flex items-center gap-1.5">
                        <Search className="h-3 w-3 text-zinc-400 shrink-0" />
                        <Input
                          ref={editInputRef}
                          value={editSearch}
                          onChange={(e) => setEditSearch(e.target.value)}
                          placeholder={`Remplacer ${l.sku}…`}
                          className="h-7 rounded-sm font-mono text-xs"
                          data-testid={`quote-editor-edit-sku-input-${idx}`}
                          onKeyDown={(e) => { if (e.key === "Escape") closeEditSku(); }}
                        />
                        <button
                          type="button"
                          onClick={closeEditSku}
                          className="h-6 w-6 inline-flex items-center justify-center text-zinc-400 hover:text-zinc-700"
                          title="Annuler le remplacement"
                          data-testid={`quote-editor-edit-sku-cancel-${idx}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                      {editSearch && (editResults.length > 0 || editSearching) && (
                        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-sm shadow-lg max-h-56 overflow-y-auto" data-testid={`quote-editor-edit-sku-results-${idx}`}>
                          {editSearching && <div className="px-3 py-2 text-xs text-zinc-500">Recherche…</div>}
                          {editResults.map((it) => (
                            <button
                              key={it.sku}
                              type="button"
                              onClick={() => replaceSku(it)}
                              className="w-full px-3 py-2 hover:bg-zinc-50 text-left flex items-center justify-between gap-3 border-b border-zinc-100 last:border-0"
                              data-testid={`quote-editor-edit-sku-pick-${idx}-${it.sku}`}
                            >
                              <div className="min-w-0">
                                <p className="font-mono text-xs text-zinc-700">{it.sku}</p>
                                <p className="text-xs text-zinc-500 truncate">{it.name}</p>
                              </div>
                              <span className="text-[11px] font-mono text-zinc-700 shrink-0">{fmt(it.price, ccy)}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <span className="truncate">{l.sku}</span>
                      <button
                        type="button"
                        onClick={() => openEditSku(idx)}
                        className="h-5 w-5 inline-flex items-center justify-center text-zinc-300 hover:text-[#D42B1E] shrink-0"
                        title="Remplacer cette référence par un autre SKU"
                        data-testid={`quote-editor-edit-sku-open-${idx}`}
                      >
                        <Pencil className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </td>
                <td className="px-4 py-2 text-zinc-900">{l.name}</td>
                <td className="px-4 py-2 font-mono text-right text-zinc-600 text-xs">{fmt(l.unit_price, ccy)}</td>
                <td className="px-4 py-2 text-right">
                  <Input
                    type="number"
                    min={1}
                    value={l.quantity}
                    onChange={(e) => setQty(l.sku, e.target.value)}
                    className="h-8 w-24 text-right rounded-sm font-mono ml-auto"
                    data-testid={`quote-editor-qty-${l.sku}`}
                  />
                </td>
                <td className="px-4 py-2 font-mono text-right text-zinc-900 font-semibold">
                  {fmt(l.unit_price * l.quantity, ccy)}
                </td>
                <td className="px-4 py-2 text-right">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => removeLine(l.sku)}
                    className="h-7 w-7 text-zinc-400 hover:text-red-600"
                    data-testid={`quote-editor-remove-${l.sku}`}
                    title="Supprimer la ligne"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
            {lines.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-zinc-400">
                  BOM vide. Ajoutez au moins une ligne ci-dessous.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot className="bg-zinc-50">
            <tr className="border-t border-zinc-200">
              <td colSpan={4} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-zinc-600">
                Total estimé (avant rafraîchissement PL)
              </td>
              <td className="px-4 py-3 font-mono text-right text-zinc-900 font-semibold" data-testid="quote-editor-preview-total">
                {fmt(previewTotal, ccy)}
              </td>
              <td />
            </tr>
            {dirty && (
              <tr className="border-t border-zinc-100">
                <td colSpan={4} className="px-4 py-2 text-right text-[11px] font-mono text-zinc-500">
                  Δ vs V{quote.revision}
                </td>
                <td
                  className={`px-4 py-2 font-mono text-right text-xs font-semibold ${
                    previewTotal - initialTotal >= 0 ? "text-amber-700" : "text-green-700"
                  }`}
                  data-testid="quote-editor-delta"
                >
                  {previewTotal - initialTotal >= 0 ? "+" : ""}
                  {fmt(previewTotal - initialTotal, ccy)}
                </td>
                <td />
              </tr>
            )}
          </tfoot>
        </table>
      </div>

      {/* Add SKU autocompleter */}
      <div className="px-5 py-4 border-t border-zinc-100 bg-zinc-50/40">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-600 mb-2">Ajouter une référence</p>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-3.5 w-3.5 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher dans la price list (SKU ou libellé)…"
            className="pl-9 rounded-sm h-9"
            data-testid="quote-editor-search"
          />
          {search && (searchResults.length > 0 || searching) && (
            <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-sm shadow-lg max-h-64 overflow-y-auto" data-testid="quote-editor-search-results">
              {searching && <div className="px-3 py-2 text-xs text-zinc-500">Recherche…</div>}
              {searchResults.map((it) => (
                <button
                  key={it.sku}
                  type="button"
                  onClick={() => addSearched(it)}
                  className="w-full px-3 py-2 hover:bg-zinc-50 text-left flex items-center justify-between gap-3 border-b border-zinc-100 last:border-0"
                  data-testid={`quote-editor-search-pick-${it.sku}`}
                >
                  <div className="min-w-0">
                    <p className="font-mono text-xs text-zinc-700">{it.sku}</p>
                    <p className="text-xs text-zinc-500 truncate">{it.name}</p>
                  </div>
                  <span className="text-[11px] font-mono text-zinc-700 shrink-0">{fmt(it.price, ccy)}</span>
                  <Plus className="h-3.5 w-3.5 text-[#D42B1E] shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Save bar */}
      <div className="px-5 py-3 border-t border-zinc-100 bg-zinc-50/30 flex flex-wrap items-center justify-between gap-3">
        <Input
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Commentaire de révision (optionnel) — ex. « Ajustement client X »"
          className="rounded-sm h-9 max-w-md"
          maxLength={500}
          data-testid="quote-editor-comment"
        />
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setLines(initialLines)}
            disabled={!dirty || saving}
            className="rounded-sm border-zinc-300"
            data-testid="quote-editor-cancel"
          >
            Annuler
          </Button>
          <Button
            type="button"
            onClick={saveChanges}
            disabled={!dirty || saving || lines.length === 0}
            className="bg-[#D42B1E] hover:bg-[#B32218] rounded-sm"
            data-testid="quote-editor-save"
          >
            <Save className="h-3.5 w-3.5 mr-1.5" />
            {saving ? "Sauvegarde…" : "Sauvegarder les modifications"}
          </Button>
        </div>
      </div>

      {/* History panel */}
      {showHistory && (
        <div className="border-t border-zinc-100 bg-white" data-testid="quote-editor-history">
          <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
            <p className="text-[11px] font-semibold tracking-[0.15em] uppercase text-zinc-600">Historique des révisions</p>
            <span className="text-[11px] font-mono text-zinc-500">{revisions.length} snapshot(s)</span>
          </div>
          <ul className="divide-y divide-zinc-100">
            {revisions.length === 0 && (
              <li className="px-5 py-4 text-sm text-zinc-500">Chargement…</li>
            )}
            {revisions.map((r) => (
              <li key={r.revision} className="px-5 py-4" data-testid={`quote-editor-revision-${r.revision}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs px-2 py-0.5 rounded-sm bg-[#D42B1E] text-white font-semibold">V{r.revision}</span>
                      <span className="text-[11px] text-zinc-500 font-mono">
                        {new Date(r.edited_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                      </span>
                      {r.edited_by_email && (
                        <span className="text-[11px] text-zinc-500">par {r.edited_by_email}</span>
                      )}
                      <span className="text-[11px] font-mono text-zinc-700">
                        {r.line_count} ligne(s) · {fmt(r.total_cost, r.currency)}
                      </span>
                    </div>
                    {r.comment && (
                      <p className="mt-1 text-sm text-zinc-700 italic">« {r.comment} »</p>
                    )}
                    {r.changes?.length > 0 && (
                      <ul className="mt-2 space-y-0.5 text-[11px] font-mono">
                        {r.changes.map((c, i) => (
                          <li key={i} className={c.op === "added" ? "text-green-700" : c.op === "removed" ? "text-red-700" : "text-amber-700"}>
                            {c.op === "added" && `+ ajout ${c.sku} (qté ${c.qty_after})`}
                            {c.op === "removed" && `– suppression ${c.sku} (était ${c.qty_before})`}
                            {c.op === "qty_changed" && `~ ${c.sku} qté ${c.qty_before} → ${c.qty_after}`}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {r.revision !== quote.revision && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => restore(r.revision)}
                      disabled={saving}
                      className="rounded-sm border-zinc-300 shrink-0"
                      data-testid={`quote-editor-restore-${r.revision}`}
                    >
                      <RotateCcw className="h-3 w-3 mr-1.5" />
                      Restaurer
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
